import { Typography } from "@hellocarbo/lasagne";
import { useTranslation } from "react-i18next";

import { TCategoryProjections, TTrajectoryCategory } from "@/types";
import { formatCarbon } from "@/utils/carbon";

function getYearTotalCarbon(
  categoryProjections: TCategoryProjections,
  year: number
) {
  return Object.keys(categoryProjections).reduce(
    (res, category) =>
      res + categoryProjections[category as TTrajectoryCategory][year],
    0
  );
}

type LateProjectionDataProps = {
  categoryProjections: TCategoryProjections;
};

function LateProjectionData({ categoryProjections }: LateProjectionDataProps) {
  const { t } = useTranslation(["common", "formatter", "trajectory"]);

  // Get all the years
  const years = Object.keys(Object.values(categoryProjections)[0]);

  // Get the tracking campaign year
  const trackingCampaignYear = Number(years[0]); // tracking campaign === first projection year

  // Get the late projection year
  const lateProjectionYear = Number(years[years.length - 1]);

  // Calculate the tracking campaign carbon
  const trackingCampaignCarbon = getYearTotalCarbon(
    categoryProjections,
    trackingCampaignYear
  );

  // Calculate the late projection carbon
  const lateProjectionCarbon = getYearTotalCarbon(
    categoryProjections,
    lateProjectionYear
  );

  const { formattedCarbon, unit } = formatCarbon(t, lateProjectionCarbon);

  // Calculate the increase ratio
  const increaseRate =
    (lateProjectionCarbon - trackingCampaignCarbon) / trackingCampaignCarbon;

  return (
    <>
      <Typography className="my-xs">
        {t("trajectory:summary.youWillEmit", { year: lateProjectionYear })}
      </Typography>
      <div className="mb-md gap-y-3xs border-grayscale-x-light px-sm py-xs flex flex-col items-center rounded border">
        <Typography step="1" weight="bold" className="text-primary">
          {formattedCarbon}
          &nbsp;
          {t(`common:unit.${unit}`)}
        </Typography>
        <Typography step="-2" className="text-grayscale">
          {t("trajectory:summary.comparedTo", {
            signedPercentage: `${increaseRate >= 0 ? "+" : ""}${t(
              "formatter:percentage",
              {
                value: increaseRate,
                formatParams: { signDisplay: "always" },
              }
            )}`,
            year: years[0],
          })}
        </Typography>
      </div>
    </>
  );
}

export default LateProjectionData;
